import React from 'react'

import { dataIdFromObject } from '@sketch/graphql-cache'
import {
  FrameGroupDetailsFragment,
  FrameDetailInfoFragment,
  useGetFramesForPageQuery,
  ArtboardDetailInfoFragment,
} from '@sketch/gql-types'

import {
  Dropdown,
  LoadPageSeparator,
  handleFetchMore,
  SFSymbolLabel,
} from '@sketch/components'

import { useVersioning } from '../../../versioning'

import { SymbolNavigationItem } from './SymbolNavigationItem'
import NavigationItem from './NavigationItem'

import { DropdownItem } from './NavigationItem.styles'
import { Container } from './FramesNavigationItem.styles'

interface FramesNavigationItemProps {
  shareIdentifier: string
  frameGroup: FrameGroupDetailsFragment
}

const FramesDropdown = ({
  frameGroup,
  shareIdentifier,
}: FramesNavigationItemProps) => {
  const pageUUID = frameGroup?.page?.uuid ?? null

  const { versionShortId, getPathname } = useVersioning()

  const { data, fetchMore, loading } = useGetFramesForPageQuery({
    variables: {
      identifier: shareIdentifier,
      versionShortId,
      pageUUID,
    },
  })

  // get frames for current page
  const frames = data?.share?.version?.document?.page?.frames?.entries ?? []

  const after =
    data?.share?.version?.document?.page?.frames?.meta?.after ?? null

  const getFrameLink = (
    frame: FrameDetailInfoFragment | ArtboardDetailInfoFragment
  ) =>
    getPathname({
      routeKey: 'FRAME',
      routeParams: {
        shareID: shareIdentifier,
        frameUUID: frame.uuid!,
      },
    })
  if (loading) {
    return (
      <>
        <Dropdown.Header>Frames</Dropdown.Header>
        <Dropdown.Skeleton />
      </>
    )
  }

  return (
    <Container>
      <Dropdown.Header>Frames</Dropdown.Header>
      {frames.map(frame => (
        <DropdownItem
          key={frame.identifier}
          isActive={match =>
            !!match || frame.identifier === frameGroup?.identifier
          }
          to={getFrameLink(frame)}
        >
          <SFSymbolLabel text={frame.name} />
        </DropdownItem>
      ))}
      {after && (
        <>
          <LoadPageSeparator
            key={after}
            loadNewPage={handleFetchMore(
              fetchMore,
              ['share', 'version', 'document', 'page', 'frames', 'entries'],
              {
                dataIdFromObject,
                after,
              }
            )}
          />
          <Dropdown.Skeleton />
        </>
      )}
    </Container>
  )
}

export const FramesNavigationItem = (props: FramesNavigationItemProps) => {
  const frameCount = props.frameGroup?.page?.frameCount || 0

  if (props.frameGroup.component) {
    return <SymbolNavigationItem {...props} />
  }

  if (frameCount === 1) {
    return <NavigationItem icon="frame" label={props.frameGroup.name || ''} />
  }

  return (
    <NavigationItem
      data-testid="artboard-selector"
      icon="frame"
      label={<SFSymbolLabel text={props.frameGroup.name} />}
      dropdownComponent={FramesDropdown}
      dropdownComponentProps={props}
    />
  )
}
